import {TranslateService} from '@ngx-translate/core';
import {Router} from '@angular/router';
import {LanguageService} from '../services/language/language.service';
import {Capacitor} from '@capacitor/core';
import {BrowserQuery} from '../queries/browser.query';
import {AppService} from '../services/app/app.service';
import {ElectronService} from '../services/electron/electron.service';
import {firstValueFrom} from 'rxjs';
import {PlatformConfig} from '../models/platform-config.model';
import {Type} from '@angular/core';
import {TraceService} from '@sentry/angular';
import {CheckedOutDocumentService} from '../services/checked-out-document/checked-out-document.service';
import {NavigationService} from '../services/navigation/navigation.service';
import {AuthQuery} from '../queries/auth.query';
import {UserQuery} from '../queries/user.query';
import {SystemInfoService} from '../services/system-info/system-info.service';

export function platformDependentClass(config: PlatformConfig): Type<unknown> {
    let platform: string;
    if (ElectronService.isElectronApp()) {
        platform = ElectronService.isWindows() ? 'electronWindows' : 'electronMac';
    } else {
        platform = Capacitor.getPlatform();
    }
    switch (platform) {
        case 'web':
            return config.web;
        case 'android':
            return config.android;
        case 'ios':
            return config.ios;
        case 'electronWindows':
            return config.electronWindows;
        case 'electronMac':
            return config.electronMac;
        default:
            throw new Error('Running on unsupported capacitor platform ' + platform);
    }
}

export async function appInitializerFactory(
    translate: TranslateService,
    router: Router,
    languageService: LanguageService,
    browserQuery: BrowserQuery,
    appService: AppService,
    navigationService: NavigationService,
    authQuery: AuthQuery,
    userQuery: UserQuery,
    _traceService: TraceService, // This is needed to enable tracing for sentry
    _checkedOutDocumentService: CheckedOutDocumentService, // needed to have the initial file sync work on devices
    systemInfoService: SystemInfoService,
): Promise<void> {
    const urlSearchParameters = new URLSearchParams(document.location.search);
    appService.setPartnerId(urlSearchParameters.get('partner'));
    if (ElectronService.isElectronApp()) {
        appService.setInitialized(false);
    }

    try {
        if (authQuery.getIsLoggedIn()) {
            const loggedInUserId = authQuery.getUserId();
            const loggedInUser = userQuery.getUserById(loggedInUserId);

            if (loggedInUser) {
                languageService.setCurrentIntlLocale(new Intl.Locale(loggedInUser.languageCultureName));
            } else {
                languageService.setBrowserLocaleAsCurrentIntlLocale();
            }
        } else {
            languageService.setBrowserLocaleAsCurrentIntlLocale();
        }

        const currentIntlLocale = languageService.getCurrentIntlLocale() ?? languageService.getDefaultLocale();
        await firstValueFrom(translate.get('LANGUAGE'));
        console.log(`Successfully initialized '${currentIntlLocale.baseName}' locale.`);
    } catch (err) {
        console.error('Problem with locale initialization.', err as Error);
    }

    if (systemInfoService.isWebApp() && !systemInfoService.isSupportedBrowser()) {
        await router.navigate(['unsupported-browser']);
        return;
    }

    const urlSegments = location.pathname.split('/')
        .filter(urlPart => urlPart);
    const queryParams = Object.fromEntries(urlSearchParameters.entries());
    await router.navigate(navigationService.getModifiedUrlSegments({
        urlSegments,
        hasSmallLayout: browserQuery.hasSmallViewport()
    }), { queryParams });
}
